import { Injectable, NgZone, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import {
  IdleWarningDialogComponent,
  IdleWarningDialogResult,
} from 'src/app/shared/components/idle-warning-dialog/idle-warning-dialog.component';

/**
 * Timestamp (ms) of the last user gesture, shared by every tab of the app so
 * that working in one tab keeps the others alive.
 */
const LAST_ACTIVITY_STORAGE_KEY = 'ap_last_activity';

/**
 * Set just before a forced sign-out so the login page can explain why the user
 * landed there. Read (and cleared) by AppSideLoginComponent.
 */
export const IDLE_SIGNED_OUT_STORAGE_KEY = 'ap_idle_signed_out';

/** Message shown on the login page after a forced sign-out. */
export const IDLE_SIGNED_OUT_MESSAGE =
  'You have been signed out automatically after a period of inactivity.';

/** DOM events that count as "the user is still there". */
const ACTIVITY_EVENTS: ReadonlyArray<string> = [
  'mousedown',
  'mousemove',
  'wheel',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/** How often the idle window is evaluated, and the countdown refreshed. */
const TICK_MS = 1_000;

/** Minimum delay between two writes of the shared activity timestamp. */
const ACTIVITY_WRITE_THROTTLE_MS = 1_000;

/** Minimum delay between two keep-alive calls to the API. */
const HEARTBEAT_THROTTLE_MS = 60_000;

/**
 * Forces a sign-out once the user has been inactive for
 * `environment.idleTimeoutMinutes`, with a countdown dialog during the last
 * `environment.idleWarningSeconds`.
 *
 * The browser is the source of truth for activity (only DOM gestures count),
 * and it reports it to the API through AuthApi/heartbeat: background polling
 * and SSE streams therefore never keep a session alive on their own. The
 * backend enforces the same window server-side as a backstop.
 *
 * Started and stopped by AppComponent, following the sign-in state.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private readonly zone = inject(NgZone);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  private readonly idleTimeoutMs = Math.max(1, environment.idleTimeoutMinutes) * 60_000;
  private readonly warningMs = Math.max(0, environment.idleWarningSeconds) * 1_000;

  /** Seconds left before the forced sign-out, consumed by the warning dialog. */
  private readonly remainingSeconds = signal(0);

  private running = false;
  private tickHandle: ReturnType<typeof setInterval> | null = null;
  private lastActivityWriteAt = 0;
  private lastHeartbeatAt = 0;
  private activitySinceLastHeartbeat = false;
  private warningDialogRef: MatDialogRef<IdleWarningDialogComponent, IdleWarningDialogResult> | null = null;
  private loggingOut = false;

  private readonly onActivity = () => this.recordActivity();

  private readonly onVisibilityChange = () => {
    // Timers are throttled in background tabs: re-evaluate as soon as the tab
    // comes back, otherwise an overdue sign-out could lag by minutes.
    if (document.visibilityState === 'visible') {
      this.tick();
    }
  };

  /** Arms the watcher. Idempotent: calling it while running is a no-op. */
  start(): void {
    if (this.running || typeof window === 'undefined') return;
    this.running = true;
    this.loggingOut = false;
    this.writeActivity(Date.now());

    this.zone.runOutsideAngular(() => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.addEventListener(eventName, this.onActivity, { passive: true, capture: true });
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.tickHandle = setInterval(() => this.tick(), TICK_MS);
    });
  }

  /** Disarms the watcher and closes any open warning. */
  stop(): void {
    if (!this.running) return;
    this.running = false;

    for (const eventName of ACTIVITY_EVENTS) {
      window.removeEventListener(eventName, this.onActivity, { capture: true } as EventListenerOptions);
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.tickHandle !== null) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
    this.closeWarning();
  }

  /** Milliseconds elapsed since the last gesture, in any tab. */
  private getIdleMs(): number {
    return Date.now() - this.readLastActivity();
  }

  private readLastActivity(): number {
    const stored = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
  }

  private writeActivity(at: number): void {
    this.lastActivityWriteAt = at;
    this.activitySinceLastHeartbeat = true;
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(at));
  }

  /**
   * Records a gesture, unless the warning is on screen: at that point only an
   * explicit answer to the dialog may extend the session, otherwise brushing
   * the mouse on the way to the button would silently cancel the countdown.
   */
  private recordActivity(): void {
    if (!this.running || this.warningDialogRef) return;

    const now = Date.now();
    if (now - this.lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) return;
    this.writeActivity(now);
  }

  private tick(): void {
    if (!this.running || this.loggingOut) return;

    const idleMs = this.getIdleMs();

    if (idleMs >= this.idleTimeoutMs) {
      this.forceLogout();
      return;
    }

    const remainingMs = this.idleTimeoutMs - idleMs;
    if (this.warningMs > 0 && remainingMs <= this.warningMs) {
      this.openOrRefreshWarning(remainingMs);
      return;
    }

    // Activity happened elsewhere (another tab) while this one was warning.
    this.closeWarning();
    this.sendHeartbeatIfDue();
  }

  private openOrRefreshWarning(remainingMs: number): void {
    const seconds = Math.max(0, Math.ceil(remainingMs / 1_000));

    if (this.warningDialogRef) {
      this.zone.run(() => this.remainingSeconds.set(seconds));
      return;
    }

    this.zone.run(() => {
      this.remainingSeconds.set(seconds);
      this.warningDialogRef = this.dialog.open(IdleWarningDialogComponent, {
        disableClose: true,
        autoFocus: true,
        width: '440px',
        data: {
          remainingSeconds: this.remainingSeconds.asReadonly(),
          idleTimeoutMinutes: environment.idleTimeoutMinutes,
          warningSeconds: this.warningMs / 1_000,
        },
      });

      this.warningDialogRef.afterClosed().subscribe((result) => {
        this.warningDialogRef = null;
        if (result === 'stay') {
          this.extendSession();
        } else if (result === 'logout') {
          this.forceLogout();
        }
      });
    });
  }

  private closeWarning(): void {
    if (!this.warningDialogRef) return;
    const ref = this.warningDialogRef;
    this.warningDialogRef = null;
    this.zone.run(() => ref.close());
  }

  /** Stay signed in: restart the window and realign the server right away. */
  private extendSession(): void {
    this.writeActivity(Date.now());
    this.sendHeartbeat();
  }

  private sendHeartbeatIfDue(): void {
    if (!this.activitySinceLastHeartbeat) return;
    if (Date.now() - this.lastHeartbeatAt < HEARTBEAT_THROTTLE_MS) return;
    this.sendHeartbeat();
  }

  private sendHeartbeat(): void {
    this.lastHeartbeatAt = Date.now();
    this.activitySinceLastHeartbeat = false;
    // Failures are swallowed on purpose: a 401 is already turned into a
    // sign-out by JwtInterceptor, and a transient network error must not
    // sign anybody out.
    this.zone.run(() => this.authService.heartbeat().subscribe({ error: () => {} }));
  }

  /** Signs the user out for good — no silent Microsoft reconnection. */
  private forceLogout(): void {
    if (this.loggingOut) return;
    this.loggingOut = true;
    this.closeWarning();
    this.stop();

    this.zone.run(() => {
      sessionStorage.setItem(IDLE_SIGNED_OUT_STORAGE_KEY, '1');
      this.authService.logout(true).subscribe({ error: () => {} });
    });
  }
}
